import { referralContract } from '@daymade/api-contracts';
import { ClientInferResponseBody } from '@ts-rest/core';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useEffect, useState } from 'react';
import { tryApi } from 'utils/api/common';
import { referralApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

type TInviteesResponse = ClientInferResponseBody<
  typeof referralContract.auth.getInvitees,
  200
>;

type TInvitersResponse = ClientInferResponseBody<
  typeof referralContract.auth.getInviters,
  200
>;

export interface PlayerInvitee {
  avatar: string | null;
  name: string;
  status: 'Active' | 'Inactive';
  gracePeriodHoursRemaining: number | null;
}

const mapInviteesToPlayers = (invitees: TInviteesResponse): PlayerInvitee[] => {
  return invitees.map((invitee) => ({
    avatar: invitee.avatarLocation,
    name: invitee.firstName,
    status: invitee.isEligibleForRewards ? 'Active' : 'Inactive',
    gracePeriodHoursRemaining: invitee.gracePeriodHoursRemaining,
  }));
};

export const usePlayerInvitesAndInvitees = () => {
  const userStore = useUserStore();
  const [inviters, setInviters] = useState<TInvitersResponse | undefined>(
    undefined,
  );
  const [invitees, setInvitees] = useState<PlayerInvitee[] | undefined>(
    undefined,
  );

  useEffect(() => {
    (async () => {
      if (userStore.currentUser?.id) {
        const [invitersResponse, inviteesResponse] = await Promise.all([
          tryApi(referralApiClient.auth.getInviters()),
          tryApi(referralApiClient.auth.getInvitees()),
        ]);

        if (invitersResponse.status === HttpStatusCode.OK) {
          setInviters(invitersResponse.body);
        } else {
          console.error('Failed to fetch referral inviters', invitersResponse);
          setInviters([]);
        }

        if (inviteesResponse.status === HttpStatusCode.OK) {
          setInvitees(mapInviteesToPlayers(inviteesResponse.body));
        } else {
          console.error('Failed to fetch referral invitees', inviteesResponse);
          setInvitees([]);
        }
      }
    })();
  }, [userStore.currentUser?.id]);

  const loading = invitees === undefined || inviters === undefined;
  const resolvedInvitees = invitees ?? [];
  const resolvedInviters = (inviters ?? []).filter(
    (x) => x.gracePeriodHoursRemaining == null,
  );
  const hasReferralConnections =
    resolvedInvitees.length > 0 || resolvedInviters.length > 0;
  const isReferrer = resolvedInvitees.length > 0;

  return {
    loading,
    invitees: resolvedInvitees,
    inviters: resolvedInviters,
    hasReferralConnections,
    isReferrer,
    referrerName: resolvedInviters[0]?.firstName,
    referrerAvatarUrl: resolvedInviters[0]?.avatarLocation ?? undefined,
  };
};
